import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import { FiThumbsDown, FiThumbsUp } from "react-icons/fi";
import { FaReply } from "react-icons/fa6"; // Import FaReply icon
import { toast } from "react-toastify"; // Import toast
import { ToastContainer } from "react-toastify"; // Import ToastContainer
import "react-toastify/dist/ReactToastify.css"; // Import ToastContainer styles
import LoginPromptModal from "@/components/homes/heros/LoginPromptModal"; // Import LoginPromptModal

export default function ReviewSection({
  userReviews,
  setUserReviews,
  userRating,
  setUserRating,
  reviewContent,
  setReviewContent,
  hasVoted,
  setHasVoted,
  API_URL,
  postId,
  title,
  isLoggedIn,
  userId,
  userName,
}) {
  const [editingReviewId, setEditingReviewId] = useState(null);
  const [editingContent, setEditingContent] = useState("");
  const [editingRating, setEditingRating] = useState(0);
  const [menuOpen, setMenuOpen] = useState(null);
  const [replyingToReviewId, setReplyingToReviewId] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [visibleReplies, setVisibleReplies] = useState({}); // Track visible replies for each review
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyContent, setEditingReplyContent] = useState("");
  const [replyMenuOpen, setReplyMenuOpen] = useState(null); // Track which reply menu is open
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false); // Track login modal state

  const hasUserReviewed = userReviews.some((review) => review.user === userId); // Check if the user has already reviewed

  const toggleRepliesVisibility = (reviewId) => {
    setVisibleReplies((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }));
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();

    if (!isLoggedIn) {
      setIsLoginModalOpen(true); // Show login modal if not logged in
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user: userId,
          postId,
          name: userName,
          title: title,
          content: reviewContent.content,
          rating: userRating,
        }),
      });

      const result = await response.json();
      if (response.ok) {
        setUserReviews((prevReviews) => [...prevReviews, result.newReview]);
        setReviewContent({ content: "" });
        setUserRating(0);
      } else if (result.message === "You have already reviewed this post.") {
        toast.error(result.message); // Display toast with the error message
      } else {
        toast.error(result.message || "Error submitting review."); // Show other backend errors
      }
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("An error occurred while submitting your review.");
    }
  };

  const handleUpvote = async (reviewId) => {
    if (!isLoggedIn) {
      setIsLoginModalOpen(true); // Show login modal if not logged in
      return;
    }

    if (hasVoted[reviewId]) {
      toast.error("You have already voted on this review.");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/reviews/upvote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewId }),
      });
      const data = await res.json();
      if (res.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.map((review) =>
            review?._id === reviewId
              ? { ...review, helpful: data?.review?.helpful }
              : review
          )
        );
        setHasVoted((prev) => ({ ...prev, [reviewId]: true }));
      } else {
        console.error("Upvote failed:", data.error);
      }
    } catch (error) {
      console.error("Error during upvote:", error);
    }
  };

  const handleDownvote = async (reviewId) => {
    if (!isLoggedIn) {
      setIsLoginModalOpen(true); // Show login modal if not logged in
      return;
    }

    if (hasVoted[reviewId]) {
      toast.error("You have already voted on this review.");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/reviews/downvote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewId }),
      });
      const data = await res.json();
      if (res.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.map((review) =>
            review?._id === reviewId
              ? { ...review, notHelpful: data?.review?.notHelpful }
              : review
          )
        );
        setHasVoted((prev) => ({ ...prev, [reviewId]: true }));
      } else {
        console.error("Downvote failed:", data.error);
      }
    } catch (error) {
      console.error("Error during downvote:", error);
    }
  };

  const handleEditReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewId,
          content: editingContent,
          rating: editingRating,
          user: userId,
        }),
      });

      const result = await response.json();
      if (response.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.map((review) =>
            review._id === reviewId
              ? { ...review, content: editingContent, rating: editingRating }
              : review
          )
        );
        setEditingReviewId(null);
        setEditingContent("");
        setEditingRating(0);
      } else {
        console.error("Error editing review:", result.message);
      }
    } catch (error) {
      console.error("Error editing review:", error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reviewId, user: userId }),
      });

      const result = await response.json();
      if (response.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.filter((review) => review._id !== reviewId)
        );
      } else {
        console.error("Error deleting review:", result.message);
      }
    } catch (error) {
      console.error("Error deleting review:", error);
    }
  };

  const handleReplySubmit = async (reviewId) => {
    if (!isLoggedIn) {
      setIsLoginModalOpen(true); // Show login modal if not logged in
      return;
    }

    if (!replyContent.trim()) {
      toast.error("Reply content cannot be empty.");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/reviews/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewId,
          content: replyContent,
          user: userId,
          name: userName,
        }),
      });

      const result = await response.json();
      if (response.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.map((review) =>
            review._id === reviewId
              ? {
                  ...review,
                  replies: [
                    ...(review.replies || []),
                    result.review.replies.at(-1),
                  ],
                }
              : review
          )
        );
        setReplyingToReviewId(null);
        setReplyContent("");
        toast.success("Reply added successfully.");
      } else {
        console.error("Error submitting reply:", result.message);
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
    }
  };

  const handleEditReply = async (reviewId, replyId) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/reply/edit`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewId,
          replyId,
          content: editingReplyContent,
          user: userId, // Ensure the user ID is sent
        }),
      });

      const result = await response.json();
      if (response.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.map((review) =>
            review._id === reviewId
              ? {
                  ...review,
                  replies: review.replies.map((reply) =>
                    reply._id === replyId
                      ? { ...reply, content: editingReplyContent }
                      : reply
                  ),
                }
              : review
          )
        );
        setEditingReplyId(null);
        setEditingReplyContent("");
        toast.success("Reply updated successfully.");
      } else {
        toast.error(result.message || "Error editing reply.");
      }
    } catch (error) {
      console.error("Error editing reply:", error);
      toast.error("An error occurred while editing the reply.");
    }
  };

  const handleDeleteReply = async (reviewId, replyId) => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/reply/delete`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reviewId,
          replyId,
          user: userId, // Ensure the user ID is sent
        }),
      });

      const result = await response.json();
      if (response.ok) {
        setUserReviews((prevReviews) =>
          prevReviews.map((review) =>
            review._id === reviewId
              ? {
                  ...review,
                  replies: review.replies.filter(
                    (reply) => reply._id !== replyId
                  ),
                }
              : review
          )
        );
        toast.success("Reply deleted successfully.");
      } else {
        toast.error(result.message || "Error deleting reply.");
      }
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast.error("An error occurred while deleting the reply.");
    }
  };

  useEffect(() => {
    const fetchUserReviews = async () => {
      try {
        const res = await fetch(`${API_URL}/api/reviews/${postId}`);
        const data = await res.json();
        if (res.ok) {
          setUserReviews(data.reviews);
        } else {
          console.error("Failed to fetch reviews:", data.error);
        }
      } catch (error) {
        console.error("Error fetching reviews:", error);
      }
    };

    if (postId) {
      fetchUserReviews();
    }
  }, [postId]);

  return (
    <>
     <ToastContainer/> 
      {/* Ensure ToastContainer is always rendered */}
      {isLoginModalOpen && (
        <LoginPromptModal
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => setIsLoginModalOpen(false)}
          title="Review Section"
        />
      )}
      
      <section 
        id="review-section"
        className="flex flex-col items-center px-4 md:px-0 py-10 ml-0 md:ml-0 lg:ml-16 xl:ml-36">
        <div className="w-full max-w-screen-md">
          <h3 className="text-3xl font-bold mb-8 text-center text-[#35425E]">
            Reviews
          </h3>

          {userReviews.length > 0 ? (
            userReviews.map((review) => (
              <div
                key={review?._id}
                className="mb-6 border border-gray-200 rounded-lg p-6  shadow-sm bg-white relative  md:w-[80%] lg:w-[80%] xl:w-[80%] mx-auto"
              >
                {review.user === userId && (
                  <div className="absolute top-4 right-4">
                    <button
                      className="text-gray-500 hover:text-gray-700"
                      onClick={() =>
                        setMenuOpen((prev) =>
                          prev === review._id ? null : review._id
                        )
                      }
                    >
                      &#x22EE;
                    </button>
                    {menuOpen === review._id && (
                      <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg">
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-blue-500 hover:bg-gray-100"
                          onClick={() => {
                            setEditingReviewId(review._id);
                            setEditingContent(review.content);
                            setEditingRating(review.rating);
                            setMenuOpen(null);
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100"
                          onClick={() => {
                            handleDeleteReview(review._id);
                            setMenuOpen(null);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-start space-x-4 mb-4">
                  {review?.image ? (
                    <img
                      src={review.image}
                      alt={review?.name}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-[#FF914D] flex items-center justify-center text-white font-bold text-lg uppercase">
                      {review?.name?.[0] || "U"}
                    </div>
                  )}

                  <div className="flex flex-col">
                    <h4 className="text-base font-semibold text-gray-800 capitalize">
                      {review?.name || "Anonymous"}
                    </h4>
                    <p className="text-xs text-gray-500">
                      {review?.reviewCount || 1} reviews
                    </p>

                    <div className="flex items-center space-x-2 mt-1">
                      <div className="text-yellow-400 text-sm">
                        {"★".repeat(review?.rating || 0)}
                        <span className="text-gray-300">
                          {"★".repeat(5 - (review?.rating || 0))}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {review?.createdAt
                          ? timeAgo(new Date(review.createdAt))
                          : "just now"}
                      </span>
                    </div>
                  </div>
                </div>

                {editingReviewId === review._id ? (
                  <div className="space-y-4">
                    <textarea
                      className="w-full border border-gray-300 px-4 py-2 rounded-md focus:outline-none"
                      rows="4"
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                    />
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Rating:</span>
                      <div className="star-rating flex space-x-1">
                        {Array(5)
                          .fill(0)
                          .map((_, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setEditingRating(i + 1)}
                              className={`text-3xl ${
                                editingRating > i
                                  ? "text-yellow-400"
                                  : "text-gray-300"
                              }`}
                            >
                              ★
                            </button>
                          ))}
                      </div>
                    </div>
                    <div className="flex justify-end space-x-4 ">
                      <button
                        className="px-4 py-2 bg-[#ff9245] text-white rounded-3xl"
                        onClick={() => handleEditReview(review._id)}
                      >
                        Save
                      </button>
                      <button
                        className="px-4 py-2 bg-gray-300 text-black rounded-3xl"
                        onClick={() => setEditingReviewId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-700 text-sm leading-relaxed ml-1 mb-1">
                    {review?.content}
                  </p>
                )}

                <div className="mt-6 flex items-center justify-between">
                  <button
                    className="text-sm text-[#ff9245] font-medium hover:underline transition flex items-center gap-1"
                    onClick={() =>
                      setReplyingToReviewId(
                        replyingToReviewId === review._id ? null : review._id
                      )
                    }
                  >
                    <FaReply className="text-[#ff9245]" />
                    {replyingToReviewId === review._id
                      ? "Cancel Reply"
                      : "Reply"}
                  </button>

                  <div className="flex flex-row gap-2">
                    <button
                      className={`w-24 sm:w-auto px-4 py-2 text-sm font-medium rounded-full border transition duration-300 flex items-center justify-center gap-1 ${
                        hasVoted[review?._id]
                          ? "bg-gray-400 text-white"
                          : "bg-[#FF914D] text-white hover:bg-white hover:!text-[#FF914D] hover:ring-1 hover:ring-[#FF914D]"
                      }`}
                      onClick={() => handleUpvote(review?._id)}
                      disabled={hasVoted[review?._id]}
                    >
                      <FiThumbsUp className="text-base" />
                      <span>({review?.helpful || 0})</span>
                    </button>

                    <button
                      className={`w-24 sm:w-auto px-4 py-2 text-sm font-medium rounded-full border transition duration-300 flex items-center justify-center gap-1 ${
                        hasVoted[review?._id]
                          ? "bg-gray-400 text-white"
                          : "bg-[#FF914D] text-white hover:bg-white hover:!text-[#FF914D] hover:ring-1 hover:ring-[#FF914D]"
                      }`}
                      onClick={() => handleDownvote(review?._id)}
                      disabled={hasVoted[review?._id]}
                    >
                      <FiThumbsDown className="text-base" />
                      <span>({review?.notHelpful || 0})</span>
                    </button>
                  </div>
                </div>

                {replyingToReviewId === review._id && (
                  <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <textarea
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FF914D] focus:outline-none text-sm resize-none"
                      rows="3"
                      placeholder="Share your thoughts..."
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                    />
                    <div className="flex justify-end space-x-3">
                      <button
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition"
                        onClick={() => {
                          setReplyingToReviewId(null);
                          setReplyContent("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 text-sm font-medium text-white bg-[#FF914D] rounded-full hover:bg-[#e67d31] transition"
                        onClick={() => handleReplySubmit(review._id)}
                      >
                        Submit Reply
                      </button>
                    </div>
                  </div>
                )}

                {(review.replies || []).length > 0 && (
                  <div className="mt-6 border-t border-gray-200 pt-5 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="text-base font-semibold text-[#35425E]">
                        Replies
                      </h4>
                      <button
                        className="text-sm text-[#005F59] font-medium hover:underline transition"
                        onClick={() => toggleRepliesVisibility(review._id)}
                      >
                        {visibleReplies[review._id]
                          ? "Hide Replies"
                          : "View All Replies"}
                      </button>
                    </div>
                    {visibleReplies[review._id] &&
                      (review.replies || []).map((reply, index) => (
                        <div
                          key={reply._id || index}
                          className="flex items-start space-x-3 p-4 bg-gray-50 border border-gray-200 rounded-lg relative"
                        >
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#FF914D] flex items-center justify-center text-white font-bold text-sm uppercase">
                            {reply?.name?.[0] || "U"}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-1">
                              <div>
                                <h5 className="text-sm font-semibold text-gray-800 capitalize">
                                  {reply.name || "Anonymous"}
                                </h5>
                                <span className="text-xs text-gray-400">
                                  {reply.createdAt
                                    ? timeAgo(new Date(reply.createdAt))
                                    : "just now"}
                                </span>
                              </div>
                              {reply.user === userId && (
                                <div className="relative">
                                  <button
                                    className="text-gray-500 hover:text-gray-700"
                                    onClick={() =>
                                      setReplyMenuOpen((prev) =>
                                        prev === reply._id ? null : reply._id
                                      )
                                    }
                                  >
                                    &#x22EE;
                                  </button>
                                  {replyMenuOpen === reply._id && (
                                    <div className="absolute right-0 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                                      <button
                                        className="block w-full text-left px-4 py-2 text-sm text-blue-500 hover:bg-gray-100"
                                        onClick={() => {
                                          setEditingReplyId(reply._id);
                                          setEditingReplyContent(reply.content);
                                          setReplyMenuOpen(null);
                                        }}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-100"
                                        onClick={() => {
                                          handleDeleteReply(
                                            review._id,
                                            reply._id
                                          );
                                          setReplyMenuOpen(null);
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            {editingReplyId === reply._id ? (
                              <div className="space-y-3">
                                <textarea
                                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#FF914D] focus:outline-none text-sm resize-none"
                                  rows="2"
                                  value={editingReplyContent}
                                  onChange={(e) =>
                                    setEditingReplyContent(e.target.value)
                                  }
                                />
                                <div className="flex justify-end space-x-3">
                                  <button
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition"
                                    onClick={() => setEditingReplyId(null)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    className="px-4 py-2 text-sm font-medium text-white bg-[#FF914D] rounded-full hover:bg-[#e67d31] transition"
                                    onClick={() =>
                                      handleEditReply(review._id, reply._id)
                                    }
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-700">
                                {reply.content}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="text-center text-gray-500 mb-6">
              No reviews yet. Be the first to write one!
            </p>
          )}

          <h3 className="text-2xl font-semibold mt-10 mb-6 text-center text-[#35425E]">
            Write a Review
          </h3>

          <form
            onSubmit={handleReviewSubmit}
            className="space-y-6 max-w-lg mx-auto bg-white p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center justify-center">
              <span className="text-lg font-semibold text-[#35425E] mr-3">
                Rating:
              </span>
              <div className="flex space-x-1">
                {Array(5)
                  .fill(0)
                  .map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setUserRating(i + 1)}
                      className={`text-3xl transition ${
                        userRating > i ? "text-yellow-400" : "text-gray-300"
                      }`}
                    >
                      ★
                    </button>
                  ))}
              </div>
            </div>

            <div>
              <textarea
                className="w-full border border-gray-300 px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF914D] transition"
                rows="4"
                placeholder="Write your review..."
                value={reviewContent.content}
                onChange={(e) =>
                  setReviewContent((prev) => ({
                    ...prev,
                    content: e.target.value,
                  }))
                }
                required
              />
            </div>

            <div className="flex justify-center">
              <button
                type="submit"
                className="w-full md:w-auto py-3 px-6 rounded-md bg-[#FF914D] text-white font-semibold hover:bg-white hover:!text-[#FF914D] hover:ring-1 hover:ring-[#FF914D] transition"
              >
                Submit Review
              </button>
            </div>
          </form>
        </div>
      </section>
    </>
  );
}

function timeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);
  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "week", seconds: 604800 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 },
  ];

  for (let interval of intervals) {
    const count = Math.floor(seconds / interval.seconds);
    if (count > 0) {
      return `${count} ${interval.label}${count !== 1 ? "s" : ""} ago`;
    }
  }
  return "just now";
}
